import { useEffect, useState } from 'react';
import toast from 'react-hot-toast';
import { Dialog } from '@/components/ui/Dialog';
import { Button } from '@/components/ui/Button';
import { Checkbox } from '@/components/ui/Checkbox';
import { Input, Label, Select } from '@/components/ui/Input';
import { useOutreachActions, type OutreachAgent } from './api';

interface Props {
  open: boolean;
  onClose: () => void;
  workspaceId?: string | null;
  agent?: OutreachAgent | null;
  onSaved?: (agent: OutreachAgent) => void;
}

export function OutreachAgentFormDialog({ open, onClose, workspaceId, agent, onSaved }: Props) {
  const { createAgent, updateAgent } = useOutreachActions(workspaceId);
  const [name, setName] = useState('');
  const [ownerName, setOwnerName] = useState('');
  const [proxyCountry, setProxyCountry] = useState('FR');
  const [proxySession, setProxySession] = useState('');
  const [dailyLimit, setDailyLimit] = useState(10);
  const [novncUrl, setNovncUrl] = useState('');
  const [provision, setProvision] = useState(true);
  const editing = !!agent;

  useEffect(() => {
    if (!open) return;
    setName(agent?.name ?? '');
    setOwnerName(agent?.owner_name ?? '');
    setProxyCountry(agent?.proxy_country ?? 'FR');
    setProxySession(agent?.proxy_session ?? '');
    setDailyLimit(agent?.daily_limit ?? 10);
    setNovncUrl(agent?.novnc_url ?? '');
    setProvision(!agent);
  }, [agent, open]);

  async function handleSubmit() {
    if (!workspaceId) {
      toast.error('Selectionne un workspace avant de creer un agent.');
      return;
    }

    try {
      const body = {
        name: name.trim() || undefined,
        owner_name: ownerName.trim() || undefined,
        proxy_country: proxyCountry.trim() || undefined,
        proxy_session: proxySession.trim() || undefined,
        daily_limit: dailyLimit,
        novnc_url: novncUrl.trim() || undefined,
        provision: editing ? undefined : provision,
      };
      const savedAgent = editing && agent
        ? await updateAgent.mutateAsync({ agentId: agent.id, body })
        : await createAgent.mutateAsync(body);
      const provisioningError =
        typeof savedAgent.metadata?.provisioning_error === 'string'
          ? savedAgent.metadata.provisioning_error
          : null;
      if (provisioningError) {
        toast.error(`Agent cree, provisioning a terminer: ${provisioningError}`);
      } else {
        toast.success(editing ? `${savedAgent.name} mis a jour` : `${savedAgent.name} cree`);
      }
      onSaved?.(savedAgent);
      onClose();
    } catch (err) {
      const message =
        (err as { response?: { data?: { error?: { message?: string } } } })?.response?.data?.error
          ?.message ?? (editing ? "Mise a jour de l'agent impossible" : "Creation de l'agent impossible");
      toast.error(message);
    }
  }

  return (
    <Dialog
      open={open}
      onClose={onClose}
      title={editing ? `Modifier ${agent?.name ?? "l'agent"}` : 'Creer un agent Outreach'}
      description={
        editing
          ? "Met a jour la fiche Dispatcher de l'agent."
          : "Cree l'entree Dispatcher. Le container OpenOutreach sera provisionne ensuite."
      }
      footer={
        <>
          <Button variant="ghost" onClick={onClose}>
            Annuler
          </Button>
          <Button onClick={handleSubmit} loading={createAgent.isPending || updateAgent.isPending}>
            {editing ? 'Enregistrer' : 'Creer'}
          </Button>
        </>
      }
    >
      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
        <div className="md:col-span-2">
          <Label>Nom</Label>
          <Input
            value={name}
            onChange={(event) => setName(event.target.value)}
            placeholder="Auto: agent-0001"
            disabled={editing}
          />
          {editing && (
            <p className="mt-1 text-[11px] text-gray-400">
              Le nom lie la fiche Dispatcher au service Easypanel. Cree un nouvel agent pour changer
              d'identite runtime.
            </p>
          )}
        </div>
        <div>
          <Label>Owner</Label>
          <Input
            value={ownerName}
            onChange={(event) => setOwnerName(event.target.value)}
            placeholder="Nadir"
          />
        </div>
        <div>
          <Label>Pays proxy</Label>
          <Select value={proxyCountry} onChange={(event) => setProxyCountry(event.target.value)}>
            <option value="FR">FR</option>
            <option value="CH">CH</option>
            <option value="BE">BE</option>
            <option value="CA">CA</option>
            <option value="US">US</option>
          </Select>
        </div>
        <div>
          <Label>Session proxy</Label>
          <Input
            value={proxySession}
            onChange={(event) => setProxySession(event.target.value)}
            placeholder="packetstream-fr-0001"
          />
        </div>
        <div>
          <Label>Quota quotidien</Label>
          <Input
            type="number"
            min={0}
            max={100}
            value={dailyLimit}
            onChange={(event) => setDailyLimit(Number(event.target.value))}
          />
        </div>
        <div className="md:col-span-2">
          <Label>URL noVNC</Label>
          <Input
            value={novncUrl}
            onChange={(event) => setNovncUrl(event.target.value)}
            placeholder="https://agent0001.outreach.getfishnet.com"
          />
        </div>
        {!editing && (
          <div className="md:col-span-2 rounded-brand border border-brand-border bg-gray-50 px-3 py-3">
            <Checkbox
              checked={provision}
              onChange={(event) => setProvision(event.target.checked)}
              label="Provisionner le service Easypanel"
              description="Cree ou met a jour le service agent-000X dans le projet OpenOutreach."
            />
          </div>
        )}
      </div>
    </Dialog>
  );
}
